import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import useResizer, { SLIDE_SELECTION_CLEAR } from '../../hooks/use-resizer';
import { slideAction } from '../../store/slideSlice';
import { computeSmartArtLayout } from './layouts';
import { DEFAULT_THEME, deltaToInlineHtml } from './smartArtUtils';
import SmartArtNodeEditor from './SmartArtNodeEditor';
import { useSmartArtPersist } from './useSmartArtPersist';
import { useAnimationContext } from '../Animations/hooks/AnimationContext';
import classes from './SmartArt.module.css';

/** Trim a connector's endpoints toward each other by `gapPct` (percent of the diagonal). */
const trimConnector = (c) => {
  if (!c.gapPct) return c;
  const dx = c.x2Pct - c.x1Pct;
  const dy = c.y2Pct - c.y1Pct;
  const len = Math.hypot(dx, dy) || 1;
  const ux = dx / len;
  const uy = dy / len;
  return {
    ...c,
    x1Pct: c.x1Pct + ux * c.gapPct,
    y1Pct: c.y1Pct + uy * c.gapPct,
    x2Pct: c.x2Pct - ux * c.gapPct,
    y2Pct: c.y2Pct - uy * c.gapPct,
  };
};

export const SmartArtObject = ({
  smartArtId,
  currentSlideIdProp = null,
  isPrint = false,
  printData = null,
}) => {
  const dispatch = useDispatch();
  const [addResizer, removeResizer, dragEvent] = useResizer();
  const [isSelected, setIsSelected] = useState(false);
  const [editingNodeId, setEditingNodeId] = useState(null);
  const [containerPx, setContainerPx] = useState({ width: 0, height: 0 });
  const isDraggingRef = useRef(false);
  const containerRef = useRef(null);
  const resizeRafRef = useRef(null);

  let currentSlideId = useSelector((state) => state.slide.currentSlideId);
  if (currentSlideIdProp) currentSlideId = currentSlideIdProp;

  const storeData = useSelector(
    (state) => state.slide.slides[currentSlideId]?.smartArtObjects?.[smartArtId]
  );
  const smartArtData = printData || storeData;

  const presentMode = useSelector((state) => state.slide.presentMode);
  const activeSmartArtId = useSelector((state) => state.slide.activeSmartArtId);

  const nodes = smartArtData?.nodes || [];
  const layout = smartArtData?.layout || 'list';
  const variant = smartArtData?.variant || undefined;
  const theme = smartArtData?.theme || DEFAULT_THEME;

  const { boxes, connectors } = useMemo(
    () => computeSmartArtLayout(layout, nodes, theme, variant),
    [layout, nodes, theme, variant]
  );

  const { commitNodes } = useSmartArtPersist(smartArtId, currentSlideId);

  /**
   * Animation state. `asOneObject` builds resolve at the container (subTargetId null);
   * per-node builds resolve per box. Connectors follow the node they lead into, which is
   * what PowerPoint does with the diagram's background build step.
   */
  const { stateFor } = useAnimationContext();
  const containerAnim = stateFor(smartArtId);
  const nodeAnim = useCallback((nodeId) => stateFor(smartArtId, nodeId), [stateFor, smartArtId]);
  const connectorAnim = useCallback(
    (connector) => (connector.toNodeId ? stateFor(smartArtId, connector.toNodeId) : containerAnim),
    [stateFor, smartArtId, containerAnim]
  );

  // Rich-text commit from the per-node Quill editor: store both the delta
  // (display formatting) and the extracted plain text (layout/text pane/export).
  const finishRichEdit = useCallback(
    (nodeId, { text, delta }) => {
      setEditingNodeId((current) => (current === nodeId ? null : current));
      const existing = nodes.find((n) => n.id === nodeId);
      if (!existing) return;
      if (existing.text === text && JSON.stringify(existing.delta) === JSON.stringify(delta)) return;
      commitNodes(nodes.map((n) => (n.id === nodeId ? { ...n, text, delta } : n)));
    },
    [nodes, commitNodes]
  );

  // --- selection / drag (mirrors WordArt) ---
  const handleMouseDown = (e) => {
    if (presentMode || isPrint || e.button !== 0) return;
    if (e.target.classList?.contains('resizer')) return;
    if (e.target.dataset?.control) return; // +/- buttons handle themselves
    // A mousedown inside the active node editor belongs to Quill (place cursor /
    // select text) — leave it alone. Anywhere else while editing exits edit mode
    // first (committing the node), then proceeds to select/drag the box.
    if (editingNodeId) {
      if (e.target.closest?.('[data-smartart-node-editor]')) return;
      setEditingNodeId(null);
    }
    e.stopPropagation();
    setIsSelected(true);
    dispatch(slideAction.setActiveSmartArt(smartArtId));
    isDraggingRef.current = true;
    dragEvent(e);
  };

  const handleClick = (e) => {
    if (presentMode || isPrint) return;
    if (e.target.classList?.contains('resizer')) return;
    e.stopPropagation();
    setIsSelected(true);
    dispatch(slideAction.setActiveSmartArt(smartArtId));
  };

  const startEditingNode = (e, nodeId) => {
    if (presentMode || isPrint) return;
    e.stopPropagation();
    e.preventDefault();
    setIsSelected(true);
    setEditingNodeId(nodeId);
  };

  useEffect(() => {
    const el = containerRef.current;
    if (!el || presentMode || isPrint) return;
    if (isSelected) addResizer(el);
    else removeResizer(el);
    return () => removeResizer(el);
  }, [isSelected, presentMode, isPrint, addResizer, removeResizer]);

  useEffect(() => {
    const onDocumentMouseUp = () => {
      if (!isDraggingRef.current) return;
      requestAnimationFrame(() => {
        isDraggingRef.current = false;
        const el = containerRef.current;
        if (el && isSelected) addResizer(el);
      });
    };
    document.addEventListener('mouseup', onDocumentMouseUp);
    return () => document.removeEventListener('mouseup', onDocumentMouseUp);
  }, [isSelected, addResizer]);

  useEffect(() => {
    const onSelectionClear = (e) => {
      if (e.detail?.exceptId === smartArtId) return;
      setIsSelected(false);
      setEditingNodeId(null);
      if (activeSmartArtId === smartArtId) dispatch(slideAction.setActiveSmartArt(null));
    };
    window.addEventListener(SLIDE_SELECTION_CLEAR, onSelectionClear);
    return () => window.removeEventListener(SLIDE_SELECTION_CLEAR, onSelectionClear);
  }, [smartArtId, activeSmartArtId, dispatch]);

  useEffect(() => {
    const handlePointerDownOutside = (e) => {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target)) return;
      if (e.target.classList?.contains('resizer')) return;
      // clicks inside the SmartArt text pane must not deselect the object
      if (e.target.closest?.('[data-smartart-pane]')) return;
      // nor clicks on the node editor's Quill toolbar (lives in #quillToolbar)
      if (e.target.closest?.('[data-smartart-node-toolbar]')) return;
      setEditingNodeId(null);
      setIsSelected(false);
      if (activeSmartArtId === smartArtId) dispatch(slideAction.setActiveSmartArt(null));
    };
    document.addEventListener('mousedown', handlePointerDownOutside, true);
    return () => document.removeEventListener('mousedown', handlePointerDownOutside, true);
  }, [activeSmartArtId, smartArtId, dispatch]);

  // track container pixel size so node font-size fits its box
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const measure = () => {
      if (resizeRafRef.current) return;
      resizeRafRef.current = requestAnimationFrame(() => {
        resizeRafRef.current = null;
        // offsetWidth/Height give the layout box, ignoring any rotate()
        // transform. getBoundingClientRect() would return the inflated
        // axis-aligned bounding box of a rotated element, scaling the contents.
        const width = el.offsetWidth;
        const height = el.offsetHeight;
        setContainerPx((prev) =>
          prev.width === width && prev.height === height ? prev : { width, height }
        );
      });
    };
    measure();
    if (typeof ResizeObserver !== 'undefined') {
      const observer = new ResizeObserver(measure);
      observer.observe(el);
      return () => {
        observer.disconnect();
        if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
      };
    }
    return () => {
      if (resizeRafRef.current) cancelAnimationFrame(resizeRafRef.current);
    };
  }, []);

  const nodeFontSize = (box) => {
    const boxH = (box.hPct / 100) * containerPx.height;
    const boxW = (box.wPct / 100) * containerPx.width;
    const size = Math.min(boxH * 0.34, boxW * 0.16, 18);
    return Math.max(8, Math.round(size || 12));
  };

  if (!smartArtData) return null;

  const interactive = !presentMode && !isPrint;

  const hasBlockArrows = connectors.some((c) => c.kind === 'blockArrow');

  /** Build a PowerPoint-style filled block-arrow polygon (pixel coords) for a trimmed connector. */
  const buildBlockArrow = (c) => {
    const ax = (c.x1Pct / 100) * containerPx.width;
    const ay = (c.y1Pct / 100) * containerPx.height;
    const bx = (c.x2Pct / 100) * containerPx.width;
    const by = (c.y2Pct / 100) * containerPx.height;
    const dx = bx - ax;
    const dy = by - ay;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;
    const nx = -uy; // unit normal
    const ny = ux;
    const base = Math.min(containerPx.width, containerPx.height) || 100;
    const shaft = base * 0.03;
    const headW = base * 0.075;
    const headLen = Math.min(base * 0.06, len * 0.7);
    const hbx = bx - ux * headLen; // head base (where shaft meets arrowhead)
    const hby = by - uy * headLen;
    const pt = (px, py, off) => `${px + nx * off},${py + ny * off}`;
    return [
      pt(ax, ay, shaft / 2),
      pt(hbx, hby, shaft / 2),
      pt(hbx, hby, headW / 2),
      `${bx},${by}`,
      pt(hbx, hby, -headW / 2),
      pt(hbx, hby, -shaft / 2),
      pt(ax, ay, -shaft / 2),
    ].join(' ');
  };

  const renderShape = (box) => {
    const stroke = 'rgba(0,0,0,0.12)';
    if (box.shapeType === 'oval') {
      return (
        <svg className={classes.nodeShape} viewBox="0 0 100 100" preserveAspectRatio="none">
          <ellipse cx="50" cy="50" rx="49" ry="49" fill={box.fill} stroke={stroke} strokeWidth="1" />
        </svg>
      );
    }
    if (box.shapeType === 'trapezoid') {
      return (
        <svg className={classes.nodeShape} viewBox="0 0 100 100" preserveAspectRatio="none">
          <polygon points="18,2 82,2 100,98 0,98" fill={box.fill} stroke={stroke} strokeWidth="1" />
        </svg>
      );
    }
    return (
      <svg className={classes.nodeShape} viewBox="0 0 100 100" preserveAspectRatio="none">
        <rect x="1" y="1" width="98" height="98" rx="6" ry="6" fill={box.fill} stroke={stroke} strokeWidth="1" />
      </svg>
    );
  };

  return (
    <div
      ref={containerRef}
      id={smartArtId}
      tabIndex={0}
      className={`${classes.smartArtContainer} smartart`}
      style={{ ...smartArtData.styles, ...containerAnim.style }}
      onMouseDown={interactive ? handleMouseDown : undefined}
      onClick={interactive ? handleClick : undefined}
    >
      <div className={classes.smartArtInner}>
        <svg
          className={classes.connectorLayer}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            <marker
              id={`arrow-${smartArtId}`}
              markerWidth="6"
              markerHeight="6"
              refX="4"
              refY="3"
              orient="auto"
              markerUnits="strokeWidth"
            >
              <path d="M0,0 L6,3 L0,6 Z" fill={theme.connectorColor || '#9aa0a6'} />
            </marker>
          </defs>
          {connectors.map((raw, i) => {
            if (raw.kind === 'blockArrow') return null;
            const c = trimConnector(raw);
            return (
              <line
                key={i}
                style={connectorAnim(raw).style}
                x1={c.x1Pct}
                y1={c.y1Pct}
                x2={c.x2Pct}
                y2={c.y2Pct}
                stroke={theme.connectorColor || '#9aa0a6'}
                strokeWidth="0.6"
                vectorEffect="non-scaling-stroke"
                markerEnd={c.kind === 'arrow' ? `url(#arrow-${smartArtId})` : undefined}
              />
            );
          })}
        </svg>

        {hasBlockArrows && containerPx.width > 0 && containerPx.height > 0 && (
          <svg
            className={classes.connectorLayer}
            viewBox={`0 0 ${containerPx.width} ${containerPx.height}`}
            preserveAspectRatio="none"
          >
            {connectors.map((raw, i) =>
              raw.kind === 'blockArrow' ? (
                <polygon
                  key={i}
                  style={connectorAnim(raw).style}
                  points={buildBlockArrow(trimConnector(raw))}
                  fill={theme.connectorColor || raw.fill || '#9aa0a6'}
                />
              ) : null
            )}
          </svg>
        )}

        {boxes.map((box) => {
          const isEditing = editingNodeId === box.nodeId;
          const node = nodes.find((n) => n.id === box.nodeId);
          return (
            <div
              key={box.nodeId}
              className={classes.nodeBox}
              style={{
                left: `${box.xPct}%`,
                top: `${box.yPct}%`,
                width: `${box.wPct}%`,
                height: `${box.hPct}%`,
                ...nodeAnim(box.nodeId).style,
              }}
            >
              {renderShape(box)}
              {isEditing ? (
                <div
                  data-smartart-node-editor
                  className={`${classes.nodeText} ${classes.nodeTextEditing} ${classes.nodeEditorHost}`}
                  style={{ color: box.textColor, fontSize: nodeFontSize(box) }}
                >
                  <SmartArtNodeEditor
                    initialDelta={node?.delta}
                    initialText={node?.text || ''}
                    onCommit={(payload) => finishRichEdit(box.nodeId, payload)}
                  />
                </div>
              ) : node?.delta ? (
                <div
                  className={classes.nodeText}
                  style={{ color: box.textColor, fontSize: nodeFontSize(box) }}
                  onDoubleClick={interactive ? (e) => startEditingNode(e, box.nodeId) : undefined}
                  dangerouslySetInnerHTML={{ __html: deltaToInlineHtml(node.delta) }}
                />
              ) : (
                <div
                  className={classes.nodeText}
                  style={{ color: box.textColor, fontSize: nodeFontSize(box) }}
                  onDoubleClick={interactive ? (e) => startEditingNode(e, box.nodeId) : undefined}
                >
                  {node?.text || ''}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default SmartArtObject;
